import { useMemo, useState } from 'react';

import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useMutationExportCsvReport } from 'api/mutations/exportCsvReport.mutation';
import { useMutationSetWidgetColumns } from 'api/mutations/setWidgetColumns.mutation';
import { useQueryStatusChangesTableData } from 'api/queries/statusChangesTableData.query';

import {
    FilterLabels,
    FilterNames,
    getFiltersData,
} from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { statusOptions } from 'components/widgets/KeyActivities/components/filter.utils';

interface Props {
    widgetId?: string;
    globalFilterData: any;
    localFilters: any;
    compareVersionId: number | undefined;
    latestVersionId: number | undefined;
    projectId: number | null | undefined;
    contractId: number | null | undefined;
    widgetViewMode: WidgetViewMode;
    table: UseBaseTable['table'];
}

export const useStatusChangesProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    contractId,
    latestVersionId,
    projectId,
    compareVersionId,
    table,
    widgetViewMode,
}: Props) => {
    const isInPinboard = Boolean(widgetId);

    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(false);
    const [status, setStatus] = useState<readonly ISelectOption<string>[]>(statusOptions);
    const [activeTab, setActiveTab] = useState<TKeys>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ||
            WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode)
            ? (localFilters?.activeTab as TKeys)
            : 'next',
    );

    const pinboardState = { ...localFilters, activeTab };
    const pageState = { ...globalFilterData, isCriticalPath, status, activeTab, sorting: table.sorting };

    const source = isInPinboard ? pinboardState : pageState;

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.TAG_CARD_BOARD]: source?.tags
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
        });
    }, [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs]);

    const getStatusChangesTableDataPayload = {
        projectId,
        contractId,
        latestVersionId,
        compareVersionId,
        filter: {
            filters,
        },
    };

    return {
        ui: {
            state: getState({ source, compareVersionId, contractId, latestVersionId, projectId }),
            setters: {
                setIsCriticalPath,
                setStatus,
                setActiveTab,
            },
            pills: useGetPills(source, widgetId, latestVersionId),
            isInPinboard: isInPinboard,
        },
        api: {
            getStatusChangesTableData: {
                instance: useQueryStatusChangesTableData(getStatusChangesTableDataPayload),
                payload: getStatusChangesTableDataPayload,
            },
            setWidgetColumns: {
                instance: useMutationSetWidgetColumns(),
            },
            exportCsvReport: {
                instance: useMutationExportCsvReport(),
                createPayload: getExportCsvReportPayload({
                    compareVersionId,
                    contractId,
                    filters,
                    latestVersionId,
                    source,
                    projectId,
                }),
            },
        },
    };
};

const getExportCsvReportPayload = ({ compareVersionId, contractId, filters, latestVersionId, source, projectId }) => {
    return ({ reportType, fileName }) => ({
        compareVersionId: compareVersionId,
        contractId: contractId,
        filter: { filters },
        latestVersionId: latestVersionId,
        period: source.activeTab,
        projectId: projectId,
        reportType,
        fileName,
    });
};

const useGetPills = (source, widgetId, latestVersionId) => {
    const pillsData = {
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
        [FilterLabels.STATUS_CARD_BOARD]: source?.status?.map((item) => item.label) || [],
        [FilterLabels.CRITICAL_PATH_USER_CARD_BOARD]: source?.isCriticalPath ? ['Yes'] : ['No'],
    };

    const { pillsList: pills } = useGlobalFiltersHook({
        widgetId,
        latestVersionId,
        source: pillsData,
    });

    return pills;
};

const getState = ({ source, compareVersionId, contractId, latestVersionId, projectId }) => {
    return {
        // project
        projectId,
        contractId,
        latestVersionId,
        compareVersionId,

        // global filters
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,

        // local filters
        isCriticalPath: source?.isCriticalPath,
        status: source?.status,
        activeTab: source?.activeTab,
        sorting: source?.sorting,
    };
};
